import { useEffect, useState, type FormEvent } from "react";
import { useNavigate } from "react-router-dom";
import type { Session } from "@supabase/supabase-js";
import { supabase } from "../lib/supabase";
import { useToast } from "../components/Toast";
import { handoffFormSchema } from "../lib/contratoSchema";
import { isFull } from "../lib/auth";
import { PLANOS, type Handoff } from "../lib/types";
import styles from "./Handoffs.module.css";

export function Handoffs({ session }: { session: Session }) {
  const { show } = useToast();
  const nav = useNavigate();
  const email = session.user.email ?? "";
  const full = isFull(session);

  const [lista, setLista] = useState<Handoff[]>([]);
  const [convertidos, setConvertidos] = useState<Set<string>>(new Set());
  const [busy, setBusy] = useState(false);

  const [nome, setNome] = useState("");
  const [link, setLink] = useState("");
  const [plano, setPlano] = useState("");
  const [valor, setValor] = useState("");
  const [notas, setNotas] = useState("");

  async function carregar() {
    const { data, error } = await supabase
      .from("handoffs")
      .select("*")
      .order("criado_em", { ascending: false });
    if (error) {
      show("error", `Falha ao carregar handoffs: ${error.message}`);
      return;
    }
    setLista((data ?? []) as Handoff[]);
    // Marca quais handoffs já viraram contrato (referenciados no pipeline).
    const { data: pipe } = await supabase
      .from("onboarding_pipeline")
      .select("handoff_id, contrato_id")
      .not("handoff_id", "is", null);
    const conv = new Set<string>();
    for (const p of pipe ?? []) {
      if (p.handoff_id && p.contrato_id) conv.add(p.handoff_id as string);
    }
    setConvertidos(conv);
  }

  useEffect(() => {
    carregar();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  async function salvar(e: FormEvent) {
    e.preventDefault();
    const parsed = handoffFormSchema.safeParse({
      nome_cliente: nome,
      link_reuniao: link,
      plano_acordado: plano,
      valor_acordado: valor === "" ? undefined : valor,
      termos_notas: notas,
    });
    if (!parsed.success) {
      show("error", parsed.error.issues[0]?.message ?? "Dados inválidos.");
      return;
    }
    setBusy(true);
    const { error } = await supabase.from("handoffs").insert({
      nome_cliente: parsed.data.nome_cliente,
      link_reuniao: parsed.data.link_reuniao || null,
      plano_acordado: parsed.data.plano_acordado || null,
      valor_acordado: parsed.data.valor_acordado ?? null,
      termos_notas: parsed.data.termos_notas || null,
      criado_por: email,
    });
    setBusy(false);
    if (error) {
      show("error", `Falha ao salvar: ${error.message}`);
      return;
    }
    show("success", "Handoff registrado.");
    setNome(""); setLink(""); setPlano(""); setValor(""); setNotas("");
    carregar();
  }

  return (
    <div className={styles.grid}>
      <section>
        <h1>Novo handoff</h1>
        <p className="muted" style={{ marginBottom: "var(--s-5)" }}>
          Comercial registra aqui o que foi combinado. Vira o ponto de partida do contrato.
        </p>
        <form onSubmit={salvar} className={`card ${styles.form}`}>
          <label className="field">
            <span>Nome do cliente *</span>
            <input value={nome} onChange={(e) => setNome(e.target.value)} required placeholder="Ex: Clínica Duprat" />
          </label>
          <label className="field">
            <span>Link da reunião</span>
            <input value={link} onChange={(e) => setLink(e.target.value)} placeholder="https://…" />
          </label>
          <div className={styles.row2}>
            <label className="field">
              <span>Plano acordado</span>
              <select value={plano} onChange={(e) => setPlano(e.target.value)}>
                <option value="">—</option>
                {PLANOS.map((p) => <option key={p} value={p}>{p}</option>)}
              </select>
            </label>
            {full && (
              <label className="field">
                <span>Valor mensal (R$)</span>
                <input type="number" inputMode="decimal" value={valor} onChange={(e) => setValor(e.target.value)} placeholder="2500" />
              </label>
            )}
          </div>
          <label className="field">
            <span>Termos / notas do combinado</span>
            <textarea rows={4} value={notas} onChange={(e) => setNotas(e.target.value)} placeholder="Tudo que foi acertado: condições, descontos, escopo, prazos…" />
          </label>
          <button type="submit" className="btn btn-primary" disabled={busy}>
            {busy ? "Salvando…" : "Registrar handoff"}
          </button>
        </form>
      </section>

      <section>
        <h2 style={{ marginBottom: "var(--s-4)" }}>Handoffs recentes</h2>
        <div className={styles.lista}>
          {lista.length === 0 && <p className="dim">Nenhum handoff ainda.</p>}
          {lista.map((h) => {
            const convertido = convertidos.has(h.id);
            return (
              <div key={h.id} className={`card ${styles.item}`}>
                <div className={styles.itemHead}>
                  <strong>{h.nome_cliente ?? "Sem nome"}</strong>
                  {convertido
                    ? <span className="badge badge-success">Contrato criado</span>
                    : <span className="badge badge-warning">Pendente</span>}
                </div>
                <div className={styles.itemMeta}>
                  {h.plano_acordado && <span>{h.plano_acordado}</span>}
                  {full && h.valor_acordado != null && <span className="mono">R$ {Number(h.valor_acordado).toLocaleString("pt-BR")}</span>}
                  {h.criado_por && <span className="dim">por {h.criado_por}</span>}
                </div>
                {h.termos_notas && <p className={styles.notas}>{h.termos_notas}</p>}
                {!convertido && (
                  <button
                    className="btn btn-ghost"
                    onClick={() => nav(`/contratos/novo?handoff_id=${h.id}`)}
                  >
                    Criar contrato a partir deste handoff
                  </button>
                )}
              </div>
            );
          })}
        </div>
      </section>
    </div>
  );
}
